import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { z } from "zod";
import { type ConnectorIntegrationOption, createConnectorInstanceMutation, listConnectorsQueryKey } from "@/api";
import { EntityFormCard, formText, IdentityFields } from "@/components/build/EntityForm";
import { useEntitySave } from "@/components/build/useEntitySave";
import { useAppForm } from "@/components/form/form";
import { Text } from "@/components/Text";

/** My own connector for an integration (e.g. our team's Slack bot), with my own key (phase 5). */
export function IntegrationInstanceForm({ integration }: Readonly<{ integration: ConnectorIntegrationOption }>) {
  const { t } = useTranslation("connectors");
  const navigate = useNavigate();
  const text = formText(t, t);
  const { createNew } = useEntitySave(text.messages);
  const create = useMutation(createConnectorInstanceMutation());

  const form = useAppForm({
    defaultValues: { name: t("instance.defaultName", { integration: integration.name }), description: "", secret: "" },
    validators: {
      onChange: z.object({
        name: z.string().trim().min(1, t("instance.nameRequired")).max(255),
        description: z.string().max(2000),
        secret: z
          .string()
          .trim()
          .min(1, t("instance.secretRequired", { label: integration.secretLabel })),
      }),
    },
    onSubmit: ({ value }) =>
      createNew({
        request: async () =>
          (
            await create.mutateAsync({
              body: {
                integration: integration.slug,
                name: value.name.trim(),
                description: value.description.trim() || null,
                secret: value.secret.trim(),
              },
              throwOnError: true,
            })
          ).data.uuid,
        invalidate: [listConnectorsQueryKey()],
        to: (uuid) => `/build/connectors/${uuid}`,
      }),
  });

  return (
    <EntityFormCard label={t("form.label")} onSubmit={() => void form.handleSubmit()}>
      <Text variant="body-sm" tone="secondary">
        {t("instance.hint", { integration: integration.name })}
      </Text>
      <IdentityFields form={form} fields={{ name: "name", description: "description" }} labels={text.labels} />
      <form.AppField name="secret">
        {(field) => <field.SecretField label={integration.secretLabel} required />}
      </form.AppField>
      <Text variant="caption" tone="tertiary">
        {t("instance.secretHint")}
      </Text>
      <form.AppForm>
        <form.SubmitActions
          onCancel={() => navigate("/build/connectors")}
          submitLabel={t("form.create")}
          requireChanges={false}
        />
      </form.AppForm>
    </EntityFormCard>
  );
}
